import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { buildShopTheLookTemplatesByHandles } from '@theme/mini-pdp-helpers';
import { formatAtcToastMessage } from '@theme/atc-toast-message';

/**
 * Mini PDP Drawer Component
 *
 * Displays pre-rendered product content in a drawer.
 * Content is passed via a <template data-mini-pdp-content> element inside the trigger.
 *
 * @example
 * <button on:click="#mini-pdp-drawer/open" data-product-handle="product-handle">
 *   Quick View
 *   <template data-mini-pdp-content>
 *     {%- render 'mini-pdp-content', product: product -%}
 *   </template>
 * </button>
 */
const MOBILE_BREAKPOINT = 1080;
/** How long the desktop Shop the Look confirmation bar stays up (matches the mobile drawer toast). */
const TOAST_DURATION_MS = 5000;

class MiniPDPDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog', 'content'];

  /** @type {string | null} */
  #currentProductHandle = null;

  /** @type {string | null} - selector for shop-the-look products drawer to reopen on "return to look" or after ATC */
  #returnToDrawerSelector = null;

  /** @type {((e: Event) => void) | null} */
  #thumbClickHandler = null;

  /** @type {((e: Event) => void) | null} */
  #returnButtonHandler = null;

  /** @type {((e: Event) => void) | null} */
  #preloadHandler = null;

  /** @type {boolean} */
  #reopenCartDrawer = false;

  /** @type {boolean} */
  #reopenSearchDrawer = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #toastTimeoutId = null;

  /**
   * @param {string} name
   * @returns {Element | undefined}
   */
  #getRef(name) {
    const r = this.refs[name];
    return Array.isArray(r) ? r[0] : r;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener('dialog:close', this.#handleOwnClose);
    this.#setupHoverPreload();
    const viewCartBtn = /** @type {HTMLElement | undefined} */ (this.#getRef('atcToastViewCart'));
    viewCartBtn?.addEventListener('click', this.#onToastViewCartClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener('dialog:close', this.#handleOwnClose);
    const viewCartBtn = /** @type {HTMLElement | undefined} */ (this.#getRef('atcToastViewCart'));
    viewCartBtn?.removeEventListener('click', this.#onToastViewCartClick);
    this.#hideAtcToast();
    if (this.#preloadHandler) {
      document.removeEventListener('pointerenter', this.#preloadHandler, { capture: true });
      this.#preloadHandler = null;
    }
    this.#removeContentListeners();
    this.#currentProductHandle = null;
    this.#returnToDrawerSelector = null;
    this.#reopenCartDrawer = false;
    this.#reopenSearchDrawer = false;
  }

  /**
   * When the mini-PDP was opened from a drawer (cart or search), reopen that drawer on close.
   * Skipped after add-to-cart (flags cleared in #handleCartUpdate) so the cart's own
   * auto-open listener handles that path without a double-open.
   */
  #handleOwnClose = () => {
    this.#hideAtcToast();

    if (this.#reopenCartDrawer) {
      this.#reopenCartDrawer = false;
      this.#reopenSearchDrawer = false;
      const cartDrawer = document.querySelector('cart-drawer-component');
      const typed = /** @type {{ showDialog?: () => void } | null} */ (cartDrawer);
      if (typed && typeof typed.showDialog === 'function') {
        typed.showDialog();
      }
      return;
    }

    if (this.#reopenSearchDrawer) {
      this.#reopenSearchDrawer = false;
      const searchModal = document.getElementById('search-modal');
      const typed = /** @type {{ showDialog?: () => void } | null} */ (searchModal);
      if (typed && typeof typed.showDialog === 'function') {
        typed.showDialog();
      }
      return;
    }
  };

  #setupHoverPreload() {
    this.#preloadHandler = (e) => {
      const btn = e.target instanceof Element ? e.target.closest('[data-preload-srcs]') : null;
      if (!(btn instanceof HTMLElement)) return;
      const srcs = btn.dataset.preloadSrcs?.split('|').filter(Boolean) ?? [];
      srcs.forEach((src) => {
        new Image().src = src;
      });
      delete btn.dataset.preloadSrcs;
    };
    document.addEventListener('pointerenter', this.#preloadHandler, { capture: true });
  }

  #removeContentListeners() {
    const content = /** @type {HTMLElement | undefined} */ (this.#getRef('content'));
    if (content && this.#thumbClickHandler) {
      content.removeEventListener('click', this.#thumbClickHandler);
      this.#thumbClickHandler = null;
    }
    const returnBtn = /** @type {HTMLElement | undefined} */ (this.#getRef('returnButton'));
    if (returnBtn && this.#returnButtonHandler) {
      returnBtn.removeEventListener('click', this.#returnButtonHandler);
      this.#returnButtonHandler = null;
    }
  }

  /**
   * Close drawer when a product was successfully added from inside this mini PDP.
   * When opened from shop-the-look products drawer (mobile), close then reopen that drawer with checkmark.
   * On desktop shop-the-look (thumbnail column + swappable panels), the modal stays open and the
   * confirmation is shown in place instead — checkmark on the thumbnail plus the toast bar.
   * @param {CustomEvent} event - CartUpdateEvent / CartAddEvent
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    const target = event.target;
    if (!(target instanceof Node) || !this.contains(target)) return;
    if (!this.refs.dialog?.open) return;

    const productHandle =
      this.#currentProductHandle || event.detail?.data?.productInfo?.productHandle;
    const productInfo = event.detail?.data?.productInfo;
    const selectedSize =
      (productInfo?.variantTitle ?? productInfo?.sizeLabel ?? '').trim() || null;

    requestAnimationFrame(() => {
      if (!this.refs.dialog?.open) return;
      this.#reopenCartDrawer = false;
      this.#reopenSearchDrawer = false;

      // Desktop shop-the-look: keep the modal open so the customer can carry on shopping the look.
      if (this.#isDesktopShopTheLook()) {
        if (productHandle) this.#markThumbAsAdded(productHandle);
        this.#showAtcToast(selectedSize ?? undefined);
        return;
      }

      const selector = this.#returnToDrawerSelector;
      if (selector && productHandle) {
        this.closeDialog().then(() => {
          const productsDrawer = document.querySelector(selector);
          const drawerTyped =
            /** @type {{ showWithCheckmark?: (handle: string, title?: string | null, size?: string) => void } | null} */ (productsDrawer);
          if (drawerTyped && typeof drawerTyped.showWithCheckmark === 'function') {
            drawerTyped.showWithCheckmark(productHandle, null, selectedSize ?? undefined);
          }
        });
      } else {
        this.closeDialog();
      }
    });
  };

  /**
   * True when the drawer is showing the multi-product shop-the-look layout at desktop widths,
   * i.e. the thumbnail column the added-state checkmark is painted on.
   * @returns {boolean}
   */
  #isDesktopShopTheLook() {
    if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT) return false;
    const content = /** @type {HTMLElement | undefined} */ (this.#getRef('content'));
    return !!content?.querySelector('.mini-pdp--shop-the-look [data-shop-the-look-thumbnails]');
  }

  /** @returns {HTMLElement[]} */
  #getThumbs() {
    const content = /** @type {HTMLElement | undefined} */ (this.#getRef('content'));
    return /** @type {HTMLElement[]} */ (
      Array.from(content?.querySelectorAll('.mini-pdp__products-column-thumb') ?? [])
    );
  }

  /**
   * Show the added-to-cart checkmark on a product's thumbnail.
   * @param {string} productHandle
   */
  #markThumbAsAdded(productHandle) {
    for (const thumb of this.#getThumbs()) {
      if (thumb.dataset.productHandle === productHandle) thumb.classList.add('is-added');
    }
  }

  /** Clear every thumbnail's added state, so a newly opened look starts fresh. */
  #clearAddedThumbs() {
    for (const thumb of this.#getThumbs()) {
      thumb.classList.remove('is-added');
    }
  }

  /**
   * Show the confirmation bar at the top of the drawer. Mirrors the mobile products drawer toast:
   * same copy, same 5s auto-hide, same "View cart" action.
   * @param {string} [selectedSize] - Variant title (e.g. "Pistachio Multi / M") or size label.
   */
  #showAtcToast(selectedSize) {
    const toast = /** @type {HTMLElement | undefined} */ (this.#getRef('atcToast'));
    const textEl = /** @type {HTMLElement | undefined} */ (this.#getRef('atcToastText'));
    if (!toast || !textEl) return;

    if (this.#toastTimeoutId) clearTimeout(this.#toastTimeoutId);

    const fallback = this.dataset.toastFallback ?? 'Added to cart';
    textEl.textContent = formatAtcToastMessage(selectedSize, fallback);
    toast.hidden = false;
    toast.setAttribute('aria-hidden', 'false');

    this.#toastTimeoutId = setTimeout(() => {
      this.#toastTimeoutId = null;
      this.#hideAtcToast();
    }, TOAST_DURATION_MS);
  }

  /** Hide the confirmation bar and cancel any pending auto-hide. */
  #hideAtcToast() {
    if (this.#toastTimeoutId) {
      clearTimeout(this.#toastTimeoutId);
      this.#toastTimeoutId = null;
    }
    const toast = /** @type {HTMLElement | undefined} */ (this.#getRef('atcToast'));
    if (!toast) return;
    toast.hidden = true;
    toast.setAttribute('aria-hidden', 'true');
  }

  /** Close the mini PDP and open the cart drawer ("View cart" on the confirmation bar). */
  #onToastViewCartClick = () => {
    this.#hideAtcToast();
    this.closeDialog().then(() => {
      const cartDrawer = document.querySelector('cart-drawer-component');
      const cartDrawerTyped = /** @type {{ open?: () => void } | null} */ (cartDrawer);
      if (cartDrawerTyped && typeof cartDrawerTyped.open === 'function') {
        cartDrawerTyped.open();
      }
    });
  };

  /**
   * Lazy-load shop-the-look-products-drawer.js if needed, then open the products drawer.
   * @param {Event} event
   */
  async #openShopTheLookProductsDrawer(event) {
    const productsDrawer = document.querySelector('shop-the-look-products-drawer');
    if (!(productsDrawer instanceof HTMLElement)) return;
    const scriptSrc = productsDrawer.dataset.scriptSrc;
    if (!customElements.get('shop-the-look-products-drawer') && scriptSrc) {
      try {
        // @ts-expect-error Dynamic import is supported at runtime in theme script context
        await import(scriptSrc);
      } catch {
        return;
      }
    }
    const drawerTyped =
      /** @type {{ open?: (e: Event) => void } | null} */ (productsDrawer);
    if (drawerTyped && typeof drawerTyped.open === 'function') {
      drawerTyped.open(event);
    }
  }

  /**
   * @param {HTMLElement} trigger
   * @param {string | null} fallbackHandle
   * @returns {string[]}
   */
  #getTriggerHandles(trigger, fallbackHandle) {
    /** @type {string[]} */
    const handles = [];
    const seen = new Set();

    /** @param {unknown} value */
    const addHandle = (value) => {
      const normalized = String(value || '').trim();
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      handles.push(normalized);
    };

    if (fallbackHandle) addHandle(fallbackHandle);

    if (trigger.dataset.productHandles) {
      try {
        const parsed = JSON.parse(trigger.dataset.productHandles);
        if (Array.isArray(parsed)) {
          parsed.forEach((value) => addHandle(value));
        }
      } catch {
        // Ignore malformed dataset payload and keep any fallback handle.
      }
    }

    return handles;
  }

  /**
   * For Constructor hotspots that only carry product handles, fetch mini-pdp
   * fragments at click-time and attach drawer templates to the trigger.
   * No cache is used: every open performs fresh fetches.
   *
   * @param {HTMLElement} trigger
   * @param {string | null} fallbackHandle
   * @returns {Promise<string | null>}
   */
  async #hydrateTriggerTemplatesIfNeeded(trigger, fallbackHandle) {
    if (trigger.dataset.requiresProductFetch !== 'true') return fallbackHandle;

    const handles = this.#getTriggerHandles(trigger, fallbackHandle);
    if (handles.length === 0) return null;

    const lineItemPropertyKey = trigger.dataset.lineItemPropertyKey?.trim() || '';
    const lineItemPropertyValue = trigger.dataset.lineItemPropertyValue?.trim() || '';
    const lineItemPropertySkuSuffix = trigger.dataset.lineItemPropertySkuSuffix?.trim() || '';

    const templates = await buildShopTheLookTemplatesByHandles(handles, {
      shopTheLookLabel: trigger.dataset.shopTheLookLabel || trigger.getAttribute('aria-label') || '',
      lineItemProperty: lineItemPropertyKey
        ? {
            key: lineItemPropertyKey,
            value: lineItemPropertyValue,
            skuSuffix: lineItemPropertySkuSuffix,
          }
        : null,
    });

    if (!templates.resolvedHandles.length || !templates.miniPdpTemplateHtml) return null;

    trigger.querySelectorAll('template[data-mini-pdp-content], template[data-shop-the-look-products]').forEach((template) => {
      template.remove();
    });

    const miniPdpTemplate = document.createElement('template');
    miniPdpTemplate.setAttribute('data-mini-pdp-content', '');
    miniPdpTemplate.innerHTML = templates.miniPdpTemplateHtml;
    trigger.appendChild(miniPdpTemplate);

    if (templates.shopTheLookProductsTemplateHtml) {
      const shopTheLookTemplate = document.createElement('template');
      shopTheLookTemplate.setAttribute('data-shop-the-look-products', '');
      shopTheLookTemplate.innerHTML = templates.shopTheLookProductsTemplateHtml;
      trigger.appendChild(shopTheLookTemplate);
    }

    trigger.dataset.productHandle = templates.resolvedHandles[0];
    trigger.dataset.productHandles = JSON.stringify(templates.resolvedHandles);
    trigger.dataset.productCount = String(templates.resolvedHandles.length);

    const firstHandle = templates.resolvedHandles[0];
    return firstHandle || null;
  }

  /**
   * @param {Event} event
   */
  async open(event) {
    const trigger =
      event.target instanceof Element ? event.target.closest('[data-product-handle]') : null;

    if (!(trigger instanceof HTMLElement)) return;

    const initialProductHandle = trigger.dataset.productHandle || '';
    const productHandles = trigger.dataset.productHandles || '';
    let shopTheLook = trigger.dataset.shopTheLook;
    let productCount = trigger.dataset.productCount;
    let productHandle = initialProductHandle || null;
    if (!productHandle && productHandles) {
      try {
        const handles = JSON.parse(productHandles);
        productHandle = Array.isArray(handles) && handles.length > 0 ? handles[0] : null;
      } catch {
        // Ignore malformed handles payload and rely on direct productHandle.
      }
    }

    productHandle = await this.#hydrateTriggerTemplatesIfNeeded(trigger, productHandle || null);
    if (!productHandle) return;

    ({ shopTheLook, productCount } = trigger.dataset);

    const template = trigger.querySelector('template[data-mini-pdp-content]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const count = productCount ? parseInt(productCount, 10) : 1;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;

    // Shop the look with multiple products on mobile: open products drawer (lazy-load its script first)
    if (
      shopTheLook === 'true' &&
      count > 1 &&
      isMobile
    ) {
      void this.#openShopTheLookProductsDrawer(event);
      return;
    }

    // On desktop: if trigger is inside any open <dialog>, stack mini-pdp on top
    // (suppress backdrop to avoid double-darkening; parent drawer stays open).
    // On mobile: close the parent drawer first, open mini-pdp, then reopen on close.
    const enclosingOpenDialog = trigger.closest('dialog[open]');
    if (enclosingOpenDialog) {
      if (!isMobile) {
        this.#openWithTemplate(template, productHandle, trigger);
        this.refs.dialog?.setAttribute('data-stacked', '');
        this.showDialog();
        document.dispatchEvent(new DialogOpenEvent());
        return;
      }
      // Mobile: close parent drawer then open mini-pdp
      const parentDrawer = /** @type {any} */ (enclosingOpenDialog.closest('cart-drawer-component, search-modal, dialog-component'));
      if (parentDrawer?.closeDialog) {
        const isCart = !!enclosingOpenDialog.closest('cart-drawer-component');
        const isSearch = !!enclosingOpenDialog.closest('#search-modal');
        this.#reopenCartDrawer = isCart;
        this.#reopenSearchDrawer = isSearch && !isCart;
        parentDrawer.addEventListener('dialog:close', () => {
          requestAnimationFrame(() => {
            this.#openWithTemplate(template, productHandle, trigger);
            this.showDialog();
            document.dispatchEvent(new DialogOpenEvent());
          });
        }, { once: true });
        parentDrawer.closeDialog();
        return;
      }
    }

    if (trigger.hasAttribute('data-reopen-cart-drawer')) {
      this.#reopenCartDrawer = true;
    }

    if (trigger.hasAttribute('data-reopen-search-drawer')) {
      this.#reopenSearchDrawer = true;
    }

    this.#openWithTemplate(template, productHandle, trigger);
    this.showDialog();
    document.dispatchEvent(new DialogOpenEvent());
  }

  /**
   * @param {HTMLTemplateElement} template
   * @param {string} productHandle
   * @param {HTMLElement} trigger
   * @param {string} [returnToDrawerSelector]
   */
  #openWithTemplate(template, productHandle, trigger, returnToDrawerSelector) {
    this.#returnToDrawerSelector = returnToDrawerSelector || null;
    this.#applySourceTestId(trigger);
    // Clear stacked mode for normal opens; openFromShopTheLook sets it after this call.
    this.refs.dialog?.removeAttribute('data-stacked');
    this.#removeContentListeners();
    this.#updateReturnButtonLabel(trigger);
    this.#renderContent(template, productHandle, trigger);
    // Every open starts from a clean confirmation state, even when #renderContent reuses
    // the existing DOM because the same look was reopened.
    this.#hideAtcToast();
    this.#clearAddedThumbs();

    const content = /** @type {HTMLElement | undefined} */ (this.#getRef('content'));
    const thumbnailsColumn = content?.querySelector('[data-shop-the-look-thumbnails]');
    if (content && thumbnailsColumn) {
      this.#thumbClickHandler = (e) => {
        const clickTarget = e.target instanceof Element ? e.target : null;
        const thumb = clickTarget?.closest('.mini-pdp__products-column-thumb[data-product-handle]');
        if (!(thumb instanceof HTMLElement)) return;
        const handle = thumb.dataset.productHandle;
        if (handle == null) return;
        const main = content.querySelector('.mini-pdp__main');
        const blocks = main?.querySelectorAll('.mini-pdp__product-content');
        if (!blocks?.length) return;
        blocks.forEach((/** @type {HTMLElement} */ block) => {
          block.hidden = block.dataset.productHandle !== handle;
        });
        content.querySelectorAll('.mini-pdp__products-column-thumb').forEach((/** @type {HTMLElement} */ t) => {
          t.classList.toggle('is-active', t.dataset.productHandle === handle);
        });
        this.#currentProductHandle = handle;
      };
      content.addEventListener('click', this.#thumbClickHandler);
    }

    const returnBtn = /** @type {HTMLElement | undefined} */ (this.#getRef('returnButton'));
    if (returnBtn && this.#returnToDrawerSelector) {
      const returnToDrawerSelector = this.#returnToDrawerSelector;
      this.#returnButtonHandler = (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.closeDialog().then(() => {
          if (!returnToDrawerSelector) return;
          const drawer = document.querySelector(returnToDrawerSelector);
          const drawerTyped =
            /** @type {{ showDialog?: () => void } | null} */ (drawer);
          if (drawerTyped && typeof drawerTyped.showDialog === 'function') {
            drawerTyped.showDialog();
          }
        });
      };
      returnBtn.addEventListener('click', this.#returnButtonHandler);
    }
  }

  /**
   * Tags the drawer with a `data-testid` identifying which component opened it,
   * e.g. `mini-pdp-shop-the-look`, `mini-pdp-make-it-a-set`, `mini-pdp-stylitics`.
   * The source is read from the trigger's `data-mini-pdp-source` attribute; each
   * entry point sets it (Liquid attribute or JS dataset). Falls back to `unknown`.
   * @param {HTMLElement} [trigger]
   */
  #applySourceTestId(trigger) {
    const source = trigger?.dataset?.miniPdpSource?.trim() || 'unknown';
    this.setAttribute('data-testid', `mini-pdp-${source}`);
  }

  /**
   * Swap the mobile return-button label based on the trigger's context.
   * When `data-return-button-label="back"` is set on the trigger (e.g. from DY recs),
   * the button shows the localised "Back" text instead of "Return to look".
   * @param {HTMLElement} [trigger]
   */
  #updateReturnButtonLabel(trigger) {
    const returnBtn = /** @type {HTMLElement | undefined} */ (this.#getRef('returnButton'));
    if (!returnBtn) return;
    const labelEl = returnBtn.querySelector('.mini-pdp-drawer__return-button-label');
    if (!labelEl) return;

    const useBack = trigger?.dataset?.returnButtonLabel === 'back';
    labelEl.textContent = useBack
      ? (returnBtn.dataset.backLabel || 'Back')
      : (returnBtn.dataset.defaultLabel || '');
  }

  /**
   * @param {HTMLTemplateElement} template
   * @param {string} productHandle
   * @param {HTMLElement} [trigger]
   */
  #renderContent(template, productHandle, trigger) {
    const content = /** @type {HTMLElement | undefined} */ (this.#getRef('content'));
    if (!(content instanceof HTMLElement)) return;

    const incomingOutfitHandles = Array.from(
      template.content.querySelectorAll('.mini-pdp__product-content[data-product-handle]'),
      (node) => node instanceof HTMLElement ? node.dataset.productHandle : null,
    ).filter(Boolean);
    const currentOutfitHandles = Array.from(
      content.querySelectorAll('.mini-pdp__product-content[data-product-handle]'),
      (node) => node instanceof HTMLElement ? node.dataset.productHandle : null,
    ).filter(Boolean);
    const isOutfitTemplate = incomingOutfitHandles.length > 1;
    const hasSameOutfitSignature = incomingOutfitHandles.length > 0
      && incomingOutfitHandles.length === currentOutfitHandles.length
      && incomingOutfitHandles.every((handle, index) => handle === currentOutfitHandles[index]);

    if (
      this.#currentProductHandle === productHandle
      && content.hasChildNodes()
      && (!isOutfitTemplate || hasSameOutfitSignature)
    ) {
      return;
    }

    content.innerHTML = '';
    content.appendChild(template.content.cloneNode(true));
    this.#currentProductHandle = productHandle;

    // Marks the content as a shop-the-look add so product-form.js tags the cart event
    // `mini-pdp-shop-the-look`, which suppresses the generic ATC success popup: these adds are
    // confirmed by the drawer toast instead. Set for the mobile products drawer (via the trigger)
    // and for the desktop shop-the-look layout. Removed otherwise — the attribute outlives an
    // innerHTML swap, so a stale flag would silently suppress the popup on the next open.
    const fromShopTheLookDrawer = trigger?.hasAttribute('data-from-shop-the-look-drawer');
    const isDesktopShopTheLookContent =
      (typeof window === 'undefined' || window.innerWidth >= MOBILE_BREAKPOINT) &&
      !!content.querySelector('.mini-pdp--shop-the-look [data-shop-the-look-thumbnails]');

    if (fromShopTheLookDrawer || isDesktopShopTheLookContent) {
      content.setAttribute('data-from-shop-the-look', 'true');
    } else {
      content.removeAttribute('data-from-shop-the-look');
    }
  }

  /**
   * Called by shop-the-look products drawer to open mini-pdp with return-to-drawer behavior.
   * @param {Event} event
   * @param {string} returnToDrawerSelector
   */
  openFromShopTheLook(event, returnToDrawerSelector) {
    const trigger =
      event?.target instanceof Element ? event.target.closest('[data-product-handle]') : null;
    if (!(trigger instanceof HTMLElement)) return;
    const { productHandle } = trigger.dataset;
    if (!productHandle) return;
    const template = trigger.querySelector('template[data-mini-pdp-content]');
    if (!(template instanceof HTMLTemplateElement)) return;

    trigger.setAttribute('data-from-shop-the-look-drawer', 'true');
    this.#openWithTemplate(template, productHandle, trigger, returnToDrawerSelector);
    // Mark as stacked so the ::backdrop is suppressed (avoids double-darkening
    // since the STL drawer's backdrop already covers the page behind both drawers).
    this.refs.dialog?.setAttribute('data-stacked', '');
    this.showDialog();
    document.dispatchEvent(new DialogOpenEvent());
  }
}

if (!customElements.get('mini-pdp-drawer')) {
  customElements.define('mini-pdp-drawer', MiniPDPDrawerComponent);
}
